/**
 * 플레이어 모듈 레벨 — `moduleId(definitionId) → level(>=1)` 맵 (순수, 단위 테스트 대상).
 *
 * 모듈 성능의 2축 중 **레벨 축**의 SSoT. 레벨은 *인스턴스*가 아니라 *플레이어 자산*이다:
 *  - 같은 moduleId의 모든 인스턴스(등급/조각 달라도)가 같은 player level을 공유 참조한다.
 *  - 환생해도 유지된다(보유 인스턴스/등급은 소멸하지만 레벨은 영구 성장).
 *  - 강화석(upgradeStone)을 소모해 `upgrade()`로 올린다(재료 차감은 호출자 책임).
 *
 * 등급(rarity)은 ModuleInstance에 남아 인스턴스 단위로 변하고 환생 시 소멸한다 — 직교 축.
 * 최종 성능 = f(moduleId, playerLevel, instanceRarity)는 CombatSystem이 이 둘을 결합해 산출한다.
 */
export class PlayerModuleLevelController {
  private readonly levels = new Map<string, number>();
  private revisionCounter = 0;

  /** 초기 맵 주입(저장 복원). level<=1(기본값)은 저장/보유 불필요라 무시. */
  constructor(initial?: Record<string, number>) {
    if (initial) {
      for (const [id, lv] of Object.entries(initial)) {
        if (typeof lv === "number" && Number.isFinite(lv) && lv > 1) {
          this.levels.set(id, Math.floor(lv));
        }
      }
    }
  }

  get revision(): number {
    return this.revisionCounter;
  }

  /** moduleId의 플레이어 레벨. 미등록(미강화) = 1. */
  level(moduleId: string): number {
    return this.levels.get(moduleId) ?? 1;
  }

  /** 레벨 +1 후 새 레벨 반환 (재료 차감은 호출자 책임). */
  upgrade(moduleId: string): number {
    const next = this.level(moduleId) + 1;
    this.levels.set(moduleId, next);
    this.revisionCounter += 1;
    return next;
  }

  /** 치트/테스트용 — 레벨 직접 설정(최소 1). */
  setLevel(moduleId: string, level: number): void {
    const next = Math.max(1, Math.floor(level));
    const prev = this.level(moduleId);
    if (prev === next) return;
    if (next <= 1) this.levels.delete(moduleId);
    else this.levels.set(moduleId, next);
    this.revisionCounter += 1;
  }

  /** 보유 전체 제거 — 테스트/리셋용. 환생은 레벨을 유지하므로 호출하지 않는다. */
  clear(): void {
    if (this.levels.size === 0) return;
    this.levels.clear();
    this.revisionCounter += 1;
  }

  /** 영속 상태 — level>1인 항목만(기본값 1은 생략해 저장 최소화). */
  toState(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const [id, lv] of this.levels) {
      if (lv > 1) out[id] = lv;
    }
    return out;
  }

  static fromState(state: Record<string, number> | undefined): PlayerModuleLevelController {
    return new PlayerModuleLevelController(state);
  }
}
